import React, { useState, useEffect, useMemo, useCallback, forwardRef } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import { merge } from 'lodash'
import differenceWith from 'lodash/difference'
import find from 'lodash/find'
import forEach from 'lodash/forEach'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import omit from 'lodash/omit'
import toLower from 'lodash/toLower'

import { Scope } from '@galo-monitor/forms'
import cultureSchema from '@galo-monitor/forms/schemas/root/culture.schema'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { CultureActions } from '@galo-monitor/stores/culture'
import Form from '@galo-monitor/web-components/Form'
import InputColor from '@galo-monitor/web-components/InputColor'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import InputText from '@galo-monitor/web-components/InputText'

import { Container } from './theme'

const countryFieldNames = {
  '1': 'ptBr',
  '2': 'deDe',
  '3': 'esEs',
  '4': 'enUs'
}

const CultureForm = forwardRef((props, formRef) => {
  const { culture, loading, setLoading, onSuccess, onError } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()

  const { data: countries } = useGaloMonitorApi('/shared/countries')

  const [cultureNames, setCultureNames] = useState({})

  const countryOptions = useMemo(
    () => map(countries, ({ name }) => ({
      label: name,
      value: name
    })),
    [countries]
  )

  const getCountryIdByName = useCallback(
    countryName => String(find(
      countries,
      country => country.name === countryName
    )?.id),
    [countries]
  )

  const handleOnCountryChange = useCallback(
    value => {
      const [addKey] = differenceWith(value, Object.keys(cultureNames))
      const [removeKey] = differenceWith(Object.keys(cultureNames), value)

      if (addKey) {
        setCultureNames(prevState => ({
          ...prevState,
          [addKey]: ''
        }))
      }

      if (removeKey) {
        setCultureNames(omit(cultureNames, removeKey))
      }
    },
    [cultureNames]
  )

  const handleOnCultureNameChange = useCallback(
    (value, countryName) => setCultureNames(prevState => ({
      ...prevState,
      [countryName]: value
    })),
    []
  )

  const handleSubmit = useCallback(
    ({ color, countries: dataCountries, ...data }) => {
      setLoading(true)
      const cultures = {}

      forEach(
        dataCountries,
        (country, index) => {
          const { name } = data.cultures[index]
          cultures[countryFieldNames[getCountryIdByName(country)]] = name
        }
      )

      dispatch(CultureActions.saveCulture(
        {
          ...culture,
          ...data,
          ...cultures,
          color
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('culture', { howMany: 1 }),
            action: toLower(t(isEmpty(culture) ? 'created' : 'updated', { gender: 'female', howMany: 1 }))
          }))
          onSuccess()
        },
        onError
      ))
    },
    [culture, dispatch, getCountryIdByName, onError, onSuccess, setLoading, snackbar, t]
  )

  useEffect(() => {
    const initialCultureValues = {}

    culture.ptBr && merge(initialCultureValues, { 'Brazil': culture.ptBr })
    culture.deDe && merge(initialCultureValues, { 'Germany': culture.deDe })
    culture.esEs && merge(initialCultureValues, { 'Paraguay': culture.esEs })
    culture.enUs && merge(initialCultureValues, { 'United States': culture.enUs })

    setCultureNames(initialCultureValues)
  }, [culture.deDe, culture.enUs, culture.esEs, culture.ptBr])

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ cultureSchema }
      onSubmit={ handleSubmit }
    >
      <Container>
        <InputText
          name="name"
          label={ t('reference name') }
          defaultValue={ culture?.name ?? '' }
          disabled={ loading }
          fullWidth
        />

        <InputSelect
          name="countries"
          label={ t('country', { howMany: 1 }) }
          onFieldValueChange={ handleOnCountryChange }
          options={ countryOptions }
          defaultValue={ Object.keys(cultureNames) }
          disabled={ loading }
          fullWidth
          multiple
        />

        {map(Object.keys(cultureNames), (countryName, index) => (
          <Scope path={ `cultures[${ index }]` } key={ countryName }>
            <InputText
              name="name"
              label={ `${ t('name') } (${ t('country name by locale code', {
                localeCode: countryFieldNames[getCountryIdByName(countryName)] ?? 'enUs'
              }) })` }
              onFieldValueChange={ value => handleOnCultureNameChange(value, countryName) }
              defaultValue={ cultureNames?.[countryName] }
              disabled={ loading }
              fullWidth
            />
          </Scope>
        ))}

        <InputColor
          name="color"
          label={ t('color') }
          defaultValue={ culture?.color ?? '' }
          fullWidth
        />
      </Container>
    </Form>
  )
})

CultureForm.propTypes = {
  culture: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

CultureForm.defaultProps = {
  culture: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default CultureForm
